import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Activates the media belonging to the selected variant.
 *
 * Saturn's gallery previously mixed configurations: the first slide showed the single-battery
 * bike and the next five showed the dual, whichever variant was selected. Product media has no
 * native variant dimension beyond `variant.image`, which is unset here, so the mapping lives in
 * the `kewelo.variant_media` metafield and is emitted into the page as JSON.
 *
 * Matching is by option values, not variant title, so a Color x Battery product resolves
 * through the same code:
 *
 *   1. exact   — every option in `conditions` matches the current selection
 *   2. partial — the most specific subset that matches wins (a Color-only rule applies
 *                across battery choices, and a two-option rule beats it when both match)
 *   3. none    — leave the gallery alone; the product's own order is the fallback
 *
 * Quantity is never consulted, and an unchanged resolution is a no-op, so changing quantity
 * cannot move the gallery.
 *
 * @extends {Component}
 */
class VariantMediaComponent extends Component {
  /** @type {Array<{conditions: Record<string,string>, primary_media_id: string, gallery_media_ids: string[]}>} */
  #rules = [];
  #lastPrimary = null;

  connectedCallback() {
    super.connectedCallback();
    const source = this.querySelector('script[type="application/json"]');
    try {
      this.#rules = source ? JSON.parse(source.textContent || '[]') : [];
    } catch {
      this.#rules = [];
    }
    document.addEventListener(StandardEvents.productSelect, this.#update);
    document.addEventListener('change', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.productSelect, this.#update);
    document.removeEventListener('change', this.#update);
  }

  /**
   * Current selection as an option-name to option-value map, read from the picker's own
   * labelling so it matches how `conditions` is authored.
   *
   * @returns {Record<string, string>}
   */
  #selection() {
    /** @type {Record<string, string>} */
    const selected = {};
    const picker = document.querySelector('variant-picker');
    if (!picker) return selected;

    for (const group of picker.querySelectorAll('[data-option-name]')) {
      const name = group.getAttribute('data-option-name');
      const checked = group.querySelector('input:checked, select');
      if (name && checked) {
        selected[name] = /** @type {HTMLInputElement} */ (checked).value.trim();
      }
    }

    // Fall back to positional reading when the picker does not label its option groups.
    if (!Object.keys(selected).length) {
      const values = [...picker.querySelectorAll('input:checked, select')].map((el) =>
        /** @type {HTMLInputElement} */ (el).value.trim()
      );
      const names = JSON.parse(this.dataset.optionNames || '[]');
      values.forEach((v, i) => {
        if (names[i]) selected[names[i]] = v;
      });
    }
    return selected;
  }

  /** The most specific rule whose conditions are all satisfied, or null. */
  #resolve(selection) {
    let best = null;
    let bestSize = -1;
    for (const rule of this.#rules) {
      const conditions = Object.entries(rule.conditions || {});
      if (!conditions.length) continue;
      const matches = conditions.every(([name, value]) => selection[name] === value);
      if (matches && conditions.length > bestSize) {
        best = rule;
        bestSize = conditions.length;
      }
    }
    return best;
  }

  #update = () => {
    const rule = this.#resolve(this.#selection());
    if (!rule || rule.primary_media_id === this.#lastPrimary) return;
    this.#lastPrimary = rule.primary_media_id;

    const gallery = document.querySelector('[data-variant-media-target]');
    if (!gallery) return;

    const allowed = new Set(rule.gallery_media_ids || []);
    let firstVisible = null;

    for (const slide of gallery.querySelectorAll('[data-media-id]')) {
      const id = slide.getAttribute('data-media-id');
      // Media that belongs to another configuration is hidden. Media that belongs to no
      // configuration at all is shared — lifestyle and detail shots apply to both builds and
      // stay visible rather than being suppressed for not being listed.
      const owned = this.#rules.some((r) => (r.gallery_media_ids || []).includes(id));
      const show = !owned || allowed.has(id);
      slide.hidden = !show;
      if (show && !firstVisible) firstVisible = slide;
    }

    const primary = gallery.querySelector(`[data-media-id="${rule.primary_media_id}"]`);
    (primary || firstVisible)?.dispatchEvent(
      new CustomEvent('variant-media:activate', { bubbles: true })
    );
  };
}

if (!customElements.get('variant-media')) {
  customElements.define('variant-media', VariantMediaComponent);
}
